import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type ValidationErrors } from '@/shared/utils/validationUtils';
import type {
  EnterpriseType,
  LoginMethod,
} from '@/features/enterprise/api/enterpriseApi';

export interface OnboardingState {
  enterpriseTypes: EnterpriseType[];
  loginMethods: LoginMethod[];
  enterprise: {
    enterpriseName: string;
    address: string;
    addressParts: {
      line1: string;
      line2: string;
      city: string;
      state: string;
      country: string;
      zipCode: string;
    };
    brandName: string;
    brandNo: string;
    enterpriseTypeId: number;
    servicePlanCodeId: string; // static value
    primaryContact: {
      name: string;
      email: string;
      phone: string;
    };
    secondaryContact: {
      name: string;
      email: string;
      phone: string;
    } | null;
    billingContact: {
      name: string;
      email: string;
      phone: string;
    };
    tenancyModel: string;
    idv: string;
    loginMethodCode?: string;
    poNumber: string; // static
    paymentTermCodeId: string; // static
    allowedDomains?: string[];
  };
  adminUser: {
    emailAddress: string;
    phoneNumber: string;
    roleId: number; // static
    simProfileId: number; // static
    sameAsPrimary: boolean;
  };
  // Validation errors
  validationErrors: {
    profileStep: ValidationErrors;
    firstUserStep: ValidationErrors;
    billingStep: ValidationErrors;
  };
}

// Static fallback enterprise types
const FALLBACK_ENTERPRISE_TYPES: EnterpriseType[] = [
  {
    id: 1,
    enterprise_type_name: 'bank',
    description: 'Banking enterprise',
    is_enabled: true,
  },
  {
    id: 2,
    enterprise_type_name: 'fintech',
    description: 'Fintech enterprise',
    is_enabled: true,
  },
  {
    id: 3,
    enterprise_type_name: 'insurance',
    description: 'Insurance Company',
    is_enabled: true,
  },
  {
    id: 4,
    enterprise_type_name: 'other',
    description: 'Other',
    is_enabled: true,
  },
];

const initialState: OnboardingState = {
  enterpriseTypes: FALLBACK_ENTERPRISE_TYPES,
  loginMethods: [],
  enterprise: {
    enterpriseName: '',
    address: '',
    addressParts: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
    },
    brandName: '',
    brandNo: '',
    enterpriseTypeId: 0, // 0 means no selection
    servicePlanCodeId: 'ENTERPRISE_PREMIUM', // static value
    primaryContact: {
      name: '',
      email: '',
      phone: '',
    },
    secondaryContact: null,
    billingContact: {
      name: 'John Doe',
      email: 'johndoe@example.com',
      phone: '+1234567890',
    },
    tenancyModel: '',
    idv: '',
    loginMethodCode: '',
    poNumber: 'PO-2026-ACME-001', // static
    paymentTermCodeId: 'NET_30', // static
    allowedDomains: [],
  },
  adminUser: {
    emailAddress: '',
    phoneNumber: '',
    roleId: 1, // static
    simProfileId: 1001, // static
    sameAsPrimary: false,
  },
  // Initialize validation errors as empty
  validationErrors: {
    profileStep: {},
    firstUserStep: {},
    billingStep: {},
  },
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    // Enterprise Types actions
    setEnterpriseTypes: (state, action: PayloadAction<EnterpriseType[]>) => {
      // If API returns empty array, keep fallback types
      state.enterpriseTypes =
        action.payload.length > 0 ? action.payload : FALLBACK_ENTERPRISE_TYPES;
    },

    setLoginMethods: (state, action: PayloadAction<LoginMethod[]>) => {
      state.loginMethods = action.payload;
    },

    // Enterprise Profile actions
    updateEnterpriseName: (state, action: PayloadAction<string>) => {
      state.enterprise.enterpriseName = action.payload;
    },
    updateTenancyModel: (state, action: PayloadAction<string>) => {
      state.enterprise.tenancyModel = action.payload;
    },
    updateIdv: (state, action: PayloadAction<string>) => {
      state.enterprise.idv = action.payload;
    },
    updateLoginMethodCode: (state, action: PayloadAction<string>) => {
      state.enterprise.loginMethodCode = action.payload;
    },
    updateEnterpriseTypeId: (state, action: PayloadAction<number>) => {
      state.enterprise.enterpriseTypeId = action.payload;
    },
    updateAddress: (state, action: PayloadAction<string>) => {
      state.enterprise.address = action.payload;
    },
    updateAddressParts: (
      state,
      action: PayloadAction<{
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        country?: string;
        zipCode?: string;
      }>
    ) => {
      if (action.payload.line1 !== undefined)
        state.enterprise.addressParts.line1 = action.payload.line1;
      if (action.payload.line2 !== undefined)
        state.enterprise.addressParts.line2 = action.payload.line2;
      if (action.payload.city !== undefined)
        state.enterprise.addressParts.city = action.payload.city;
      if (action.payload.state !== undefined)
        state.enterprise.addressParts.state = action.payload.state;
      if (action.payload.country !== undefined)
        state.enterprise.addressParts.country = action.payload.country;
      if (action.payload.zipCode !== undefined)
        state.enterprise.addressParts.zipCode = action.payload.zipCode;
    },
    updateBrandName: (state, action: PayloadAction<string>) => {
      state.enterprise.brandName = action.payload;
    },
    updateBrandNo: (state, action: PayloadAction<string>) => {
      state.enterprise.brandNo = action.payload;
    },

    updateAllowedDomains: (state, action: PayloadAction<string[]>) => {
      state.enterprise.allowedDomains = action.payload;
    },

    // Primary Contact actions
    updatePrimaryContact: (
      state,
      action: PayloadAction<{ name?: string; email?: string; phone?: string }>
    ) => {
      if (action.payload.name !== undefined)
        state.enterprise.primaryContact.name = action.payload.name;
      if (action.payload.email !== undefined)
        state.enterprise.primaryContact.email = action.payload.email;
      if (action.payload.phone !== undefined)
        state.enterprise.primaryContact.phone = action.payload.phone;
    },

    // Secondary Contact actions
    enableSecondaryContact: (state) => {
      state.enterprise.secondaryContact = {
        name: '',
        email: '',
        phone: '',
      };
    },
    disableSecondaryContact: (state) => {
      state.enterprise.secondaryContact = null;
    },
    updateSecondaryContact: (
      state,
      action: PayloadAction<{ name?: string; email?: string; phone?: string }>
    ) => {
      if (state.enterprise.secondaryContact !== null) {
        if (action.payload.name !== undefined)
          state.enterprise.secondaryContact.name = action.payload.name;
        if (action.payload.email !== undefined)
          state.enterprise.secondaryContact.email = action.payload.email;
        if (action.payload.phone !== undefined)
          state.enterprise.secondaryContact.phone = action.payload.phone;
      }
    },

    // Billing Contact actions
    updateBillingContact: (
      state,
      action: PayloadAction<{ name?: string; email?: string; phone?: string }>
    ) => {
      if (action.payload.name !== undefined)
        state.enterprise.billingContact.name = action.payload.name;
      if (action.payload.email !== undefined)
        state.enterprise.billingContact.email = action.payload.email;
      if (action.payload.phone !== undefined)
        state.enterprise.billingContact.phone = action.payload.phone;
    },

    // Admin User actions
    updateAdminUser: (
      state,
      action: PayloadAction<{
        emailAddress?: string;
        phoneNumber?: string;
        sameAsPrimary?: boolean;
      }>
    ) => {
      if (action.payload.emailAddress !== undefined)
        state.adminUser.emailAddress = action.payload.emailAddress;
      if (action.payload.phoneNumber !== undefined)
        state.adminUser.phoneNumber = action.payload.phoneNumber;
      if (action.payload.sameAsPrimary !== undefined)
        state.adminUser.sameAsPrimary = action.payload.sameAsPrimary;
    },

    // Copy primary contact to admin user (for "Same as Primary Contact" functionality)
    copyPrimaryContactToAdminUser: (state) => {
      state.adminUser.emailAddress = state.enterprise.primaryContact.email;
      state.adminUser.phoneNumber = state.enterprise.primaryContact.phone;
      state.adminUser.sameAsPrimary = true;
    },

    // Reset onboarding state
    resetOnboarding: () => initialState,

    // Reset individual steps
    resetProfileStep: (state) => {
      state.enterprise.enterpriseName = initialState.enterprise.enterpriseName;
      state.enterprise.enterpriseTypeId =
        initialState.enterprise.enterpriseTypeId;
      state.enterprise.address = initialState.enterprise.address;
      state.enterprise.addressParts = {
        ...initialState.enterprise.addressParts,
      };
      state.enterprise.brandName = initialState.enterprise.brandName;
      state.enterprise.brandNo = initialState.enterprise.brandNo;
      state.enterprise.allowedDomains = initialState.enterprise.allowedDomains;
      state.enterprise.tenancyModel = initialState.enterprise.tenancyModel;
      state.enterprise.idv = initialState.enterprise.idv;
      state.enterprise.loginMethodCode =
        initialState.enterprise.loginMethodCode;
      state.enterprise.primaryContact = {
        ...initialState.enterprise.primaryContact,
      };
      state.enterprise.secondaryContact =
        initialState.enterprise.secondaryContact;
    },

    resetFirstUserStep: (state) => {
      state.adminUser = { ...initialState.adminUser };
    },

    resetBillingStep: (state) => {
      state.enterprise.billingContact = {
        ...initialState.enterprise.billingContact,
      };
    },

    // Validation actions
    setProfileStepErrors: (state, action: PayloadAction<ValidationErrors>) => {
      // Initialize validationErrors if it doesn't exist (for backward compatibility)
      if (!state.validationErrors) {
        state.validationErrors = {
          profileStep: {},
          firstUserStep: {},
          billingStep: {},
        };
      }
      state.validationErrors.profileStep = action.payload;
    },
    setFirstUserStepErrors: (
      state,
      action: PayloadAction<ValidationErrors>
    ) => {
      // Initialize validationErrors if it doesn't exist (for backward compatibility)
      if (!state.validationErrors) {
        state.validationErrors = {
          profileStep: {},
          firstUserStep: {},
          billingStep: {},
        };
      }
      state.validationErrors.firstUserStep = action.payload;
    },
    setBillingStepErrors: (state, action: PayloadAction<ValidationErrors>) => {
      // Initialize validationErrors if it doesn't exist (for backward compatibility)
      if (!state.validationErrors) {
        state.validationErrors = {
          profileStep: {},
          firstUserStep: {},
          billingStep: {},
        };
      }
      state.validationErrors.billingStep = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = {
        profileStep: {},
        firstUserStep: {},
        billingStep: {},
      };
    },
    // Clear specific profile step field errors atomically
    clearProfileStepFieldErrors: (state, action: PayloadAction<string[]>) => {
      if (!state.validationErrors) return;
      const keys = action.payload;
      keys.forEach((k) => {
        if (state.validationErrors.profileStep) {
          delete state.validationErrors.profileStep[k];
        }
      });
    },
  },
});

export const {
  setEnterpriseTypes,
  setLoginMethods,
  updateEnterpriseName,
  updateEnterpriseTypeId,
  updateAddress,
  updateAddressParts,
  updateBrandName,
  updateBrandNo,
  updateAllowedDomains,
  updateTenancyModel,
  updateIdv,
  updateLoginMethodCode,
  updatePrimaryContact,
  enableSecondaryContact,
  disableSecondaryContact,
  updateSecondaryContact,
  updateBillingContact,
  updateAdminUser,
  copyPrimaryContactToAdminUser,
  resetOnboarding,
  resetProfileStep,
  resetFirstUserStep,
  resetBillingStep,
  setProfileStepErrors,
  setFirstUserStepErrors,
  setBillingStepErrors,
  clearValidationErrors,
  clearProfileStepFieldErrors,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
